export interface SelectedImageItem {
	type: string
	data: any
	key: string
}

/**
 * Reconcile the dialog's thumbnails against the selection state in Vuex.
 *
 * Surviving images keep their current slots so an annotated composite or restored
 * original appears where the prior image was. Newly selected images append.
 *
 * Returns the existing array reference when membership is unchanged, so callers
 * can skip unnecessary writes.
 */
export function reconcileSelectedImages (
	local: SelectedImageItem[],
	next: SelectedImageItem[],
): SelectedImageItem[] {
	// Field-value updates can briefly clear selection state. Genuine deselection is
	// reflected in the local list directly, so do not blank the dialog transiently.
	if (local.length && !next.length) return local;

	const nextByKey = new Map(next.map((item) => [item.key, item]));
	const localKeys = new Set(local.map((item) => item.key));

	const membershipUnchanged = nextByKey.size === localKeys.size &&
		next.every((item) => localKeys.has(item.key));
	if (membershipUnchanged) return local;

	const added = next.filter((item) => !localKeys.has(item.key));
	let addedIndex = 0;
	const reconciled: SelectedImageItem[] = [];

	local.forEach((item) => {
		const survivor = nextByKey.get(item.key);
		if (survivor) {
			reconciled.push(survivor);
			return;
		}
		if (addedIndex < added.length) {
			reconciled.push(added[addedIndex]);
			addedIndex += 1;
		}
	});

	return [...reconciled, ...added.slice(addedIndex)];
}
